import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Text,
  Image,
  VStack,
  IconButton,
  HStack,
  Button,
} from '@chakra-ui/react';
import { Paperclip } from 'lucide-react';

interface FileUploadProps {
  savedFiles?: File[];
  onFilesChange?: (files: File[]) => void;
  onError: any;
  maxTotalSize?: number;
  onLimitExceeded?: (totalSize: number, maxSize: number) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  savedFiles,
  onFilesChange,
  onError,
  maxTotalSize,
  onLimitExceeded,
}) => {
  const [files, setFiles] = useState<File[]>(savedFiles || []);
  const [previews, setPreviews] = useState<Record<string, string>>({});

  useEffect(() => {
    onFilesChange?.(files);
  }, [files]);

  const onDrop = (acceptedFiles: File[]) => {
    const nextFiles = [...files, ...acceptedFiles];
    const totalSize = nextFiles.reduce((total, file) => total + file.size, 0);

    if (maxTotalSize && totalSize > maxTotalSize) {
      onLimitExceeded?.(totalSize, maxTotalSize);
      return;
    }

    setFiles(nextFiles);

    const newPreviews: Record<string, string> = {};
    acceptedFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        newPreviews[file.name] = URL.createObjectURL(file);
      }
    });

    setPreviews(prev => ({ ...prev, ...newPreviews }));
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'image/*': [], 'application/pdf': ['.pdf'] },
    multiple: true,
    noClick: files.length > 0,
    maxSize: 20971520, // 20mb
  });

  return (
    <VStack spacing={4}>
      {/* Área Dropzone que vira lista */}
      <Box
        {...getRootProps()}
        borderWidth={onError ? '3px' : '2px'}
        borderStyle="dashed"
        borderColor={
          isDragActive ? 'blue.400' : onError ? 'tomato' : 'gray.500'
        }
        p={6}
        cursor="pointer"
        w="100%"
        minW="400px"
        maxW="400px"
        borderRadius="2xl"
        minH="120px"
        display="flex"
        flexDir="column"
        justifyContent="center"
        alignItems="center"
      >
        <input {...getInputProps()} />

        {files.length === 0 ? (
          <Text textAlign="center">
            {isDragActive
              ? 'Solte os arquivos aqui...'
              : 'Arraste imagens ou PDFs, ou clique para selecionar'}
          </Text>
        ) : (
          <>
            {/* Lista dentro da Box */}
            <VStack spacing={3} w="100%">
              {files.map(file => (
                <HStack
                  key={file.name}
                  p={2}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.600"
                  w="100%"
                  justify="space-between"
                >
                  <HStack spacing={3}>
                    {file.type.startsWith('image/') && previews[file.name] && (
                      <Image
                        src={previews[file.name]}
                        alt={file.name}
                        boxSize="50px"
                        objectFit="cover"
                        borderRadius="lg"
                      />
                    )}
                    <Text fontSize="sm">{file.name}</Text>
                  </HStack>

                  {/* BOTÃO PARA REMOVER */}
                  <IconButton
                    aria-label="Remover arquivo"
                    icon={
                      <Text fontSize="md" fontWeight="bold">
                        ×
                      </Text>
                    }
                    size="sm"
                    variant="ghost"
                    borderRadius="full"
                    onClick={() => {
                      setFiles(prev => prev.filter(f => f.name !== file.name));

                      // remove preview se for imagem
                      if (previews[file.name]) {
                        URL.revokeObjectURL(previews[file.name]); // limpa da memória
                        setPreviews(prev => {
                          const copy = { ...prev };
                          delete copy[file.name];
                          return copy;
                        });
                      }
                    }}
                  />
                </HStack>
              ))}
            </VStack>

            {files.length > 0 && (
              <Button rounded="2xl" onClick={open} variant="outline" mt="2">
                ANEXAR DOCUMENTO
                <Paperclip />
              </Button>
            )}
          </>
        )}
      </Box>
    </VStack>
  );
};
